import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertCircle, AlertTriangle, ArrowDownUp, Check, CheckCircle2, Clock3, Eye,
  FileImage, Loader2, RefreshCw, Save, ShieldAlert, ShieldCheck, Users,
} from "lucide-react";
import { http, requestErrorMessage } from "@/lib/erp";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const DEFAULTS = {
  enabled: false,
  greeting_enabled: true,
  catalogue_enabled: true,
  catalogue_image_enabled: true,
  dues_enabled: true,
  timezone: "Asia/Kolkata",
  greeting_time: "09:00",
  catalogue_time: "09:30",
  catalogue_image_time: "09:45",
  dues_time: "10:00",
  default_country_code: "91",
  overdue_every_days: 7,
  greeting_template: "Hello {name}, greetings from {company}. Reply STOP to opt out.",
  catalogue_template: "Hello {name}, today's catalogue: {catalogue} {link}\nReply STOP to opt out.",
  image_caption_template: "Hello {name}, today's catalogue is attached.\nReply STOP to opt out.",
  customer_due_template: "Hello {name}, invoice {bill_number} for {amount} was due on {due_date}. Please contact us if you have already paid. Reply STOP to opt out.",
  vendor_due_template: "Hello {name}, our payable to you for bill {bill_number} is {amount}, due {due_date}. Please contact us to coordinate. Reply STOP to opt out.",
};

const ROUTES = {
  settings: "/whatsapp/automation",
  contacts: "/whatsapp/automation/contacts",
  catalogues: "/whatsapp/automation/catalogues",
  history: "/whatsapp/automation/history",
  preview: "/whatsapp/automation/preview",
};
const VARIABLE_HINT = "Available fields: {name}, {company}, {phone}, {link}, {catalogue}, {amount}, {due_date}, {bill_number}. Promotional messages require saved marketing consent and must include STOP.";
const STALE = Symbol("stale company request");

function getError(error, action) {
  return requestErrorMessage(error, action);
}

function PanelHeading({ eyebrow, title, note, icon: Icon }) {
  return <div className="flex items-start gap-3">
    {Icon && <span className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-brand-50 text-brand-700"><Icon className="h-4 w-4" /></span>}
    <div><p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{eyebrow}</p>
      <h3 className="mt-0.5 font-heading text-base font-semibold">{title}</h3>
      {note && <p className="mt-1 max-w-3xl text-sm text-muted-foreground">{note}</p>}
    </div>
  </div>;
}

function LoadState({ loading, error, retry, noun }) {
  if (loading) return <div role="status" className="flex items-center gap-2 rounded-md border bg-muted/40 px-4 py-5 text-sm text-muted-foreground">
    <span className="h-4 w-4 animate-pulse rounded-full bg-brand-200" />Loading {noun}…
  </div>;
  if (error) return <div role="alert" className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
    <span className="flex items-center gap-2"><AlertCircle className="h-4 w-4 shrink-0" />{error}</span>
    <Button type="button" size="sm" variant="outline" onClick={retry}><RefreshCw className="mr-1 h-3.5 w-3.5" />Retry</Button>
  </div>;
  return null;
}

function Toggle({ checked, onChange, label, disabled = false, detail }) {
  return <label className={`flex items-start gap-3 rounded-md border px-3 py-3 ${disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer hover:bg-muted/30"}`}>
    <input type="checkbox" checked={Boolean(checked)} disabled={disabled} onChange={(event) => onChange(event.target.checked)}
      className="mt-0.5 h-4 w-4 accent-brand-600" />
    <span><span className="block text-sm font-medium">{label}</span>{detail && <span className="mt-0.5 block text-xs text-muted-foreground">{detail}</span>}</span>
  </label>;
}

function ScheduleField({ label, name, value, onChange, disabled }) {
  return <div><Label htmlFor={`wa-auto-${name}`}>{label}</Label>
    <Input id={`wa-auto-${name}`} type="time" value={value || ""} disabled={disabled} onChange={(event) => onChange(name, event.target.value)}
      className="mt-1.5 font-mono" data-testid={`whatsapp-auto-${name}`} /></div>;
}

function ContactConsent({ contact, updateContact, busy }) {
  const [override, setOverride] = useState(contact.phone_override || "");
  const [savingPhone, setSavingPhone] = useState(false);
  const originalPhone = contact.phone || "";
  const normalized = contact.whatsapp_phone || "";
  const blocked = Boolean(contact.opted_out);
  useEffect(() => setOverride(contact.phone_override || ""), [contact.id, contact.phone_override]);
  const savePhone = async () => {
    setSavingPhone(true);
    await updateContact(contact, { phone_override: override.trim() || null });
    setSavingPhone(false);
  };
  const toggleConsent = (field, value) => {
    if (value && blocked) return;
    updateContact(contact, { [field]: value });
  };
  return <div className="grid gap-3 border-t px-4 py-4 first:border-t-0 lg:grid-cols-[minmax(170px,1fr)_minmax(230px,1.2fr)_minmax(270px,1.7fr)] lg:items-center">
    <div className="min-w-0">
      <div className="truncate font-medium">{contact.name || "Unnamed contact"}</div>
      <div className="mt-0.5 text-xs text-muted-foreground">{contact.kind === "vendor" ? "Vendor" : "Customer"} · Original: {originalPhone || "missing"}</div>
      {contact.kind === "vendor" && (contact.credit_days === null || contact.credit_days === undefined) &&
        <div className="mt-1 text-[11px] text-amber-800">Credit days are missing; vendor financial terms need updating before due timing can be determined.</div>}
      <div className="mt-1 flex flex-wrap gap-1.5">
        {contact.phone_valid === false || !normalized
          ? <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-medium text-rose-800">Invalid or missing number</span>
          : <span className="rounded-full bg-muted px-2 py-0.5 font-mono text-[11px]">WhatsApp +{normalized}</span>}
        {blocked && <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-medium text-rose-800">STOP / blocked</span>}
      </div>
    </div>
    <div className="flex flex-wrap gap-x-5 gap-y-2">
      <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={Boolean(contact.marketing_consent)} disabled={busy || blocked} onChange={(e) => toggleConsent("marketing_consent", e.target.checked)} className="h-4 w-4 accent-brand-600" />Promotional consent</label>
      <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={Boolean(contact.dues_consent)} disabled={busy || blocked} onChange={(e) => toggleConsent("dues_consent", e.target.checked)} className="h-4 w-4 accent-brand-600" />Dues consent</label>
      {blocked && <span className="text-xs text-rose-700">Blocked globally; consent cannot enable messaging.</span>}
    </div>
    <div className="flex min-w-0 gap-2">
      <div className="min-w-0 flex-1"><Label className="sr-only" htmlFor={`wa-contact-phone-${contact.kind}-${contact.id}`}>WhatsApp phone override for {contact.name}</Label>
        <Input id={`wa-contact-phone-${contact.kind}-${contact.id}`} type="tel" value={override} placeholder="Phone override, e.g. +919876543210"
          onChange={(e) => setOverride(e.target.value)} disabled={busy} className="h-9 text-xs" /></div>
      <Button type="button" size="sm" variant="outline" onClick={savePhone} disabled={busy || savingPhone || override === (contact.phone_override || "")}>
        {savingPhone ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Save phone"}
      </Button>
    </div>
  </div>;
}

function WhatsAppAutomation({ canManage, companyId }) {
  const [settings, setSettings] = useState(DEFAULTS);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [settingsError, setSettingsError] = useState("");
  const [saving, setSaving] = useState(false);
  const [savedNotice, setSavedNotice] = useState("");
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(true);
  const [contactsError, setContactsError] = useState("");
  const [contactBusy, setContactBusy] = useState({});
  const [contactNotice, setContactNotice] = useState("");
  const [catalogues, setCatalogues] = useState([]);
  const [cataloguesLoading, setCataloguesLoading] = useState(true);
  const [cataloguesError, setCataloguesError] = useState("");
  const [catalogueBusy, setCatalogueBusy] = useState({});
  const [previewBlob, setPreviewBlob] = useState(null);
  const [previewCatalogueId, setPreviewCatalogueId] = useState(null);
  const [confirmReviewed, setConfirmReviewed] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyError, setHistoryError] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(true);
  const [previewError, setPreviewError] = useState("");
  const [historyFilter, setHistoryFilter] = useState("all");
  const requestControllers = useRef(new Set());
  const alive = useRef(true);
  const blobUrlRef = useRef("");
  const isCurrentCompany = () => alive.current && Boolean(companyId) && localStorage.getItem("vg_company") === companyId;

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      requestControllers.current.forEach((controller) => controller.abort());
      requestControllers.current.clear();
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
    };
  }, []);

  const request = async (path, config = {}, method = "get", body) => {
    if (!companyId || !canManage) return STALE;
    if ((localStorage.getItem("vg_company") || "") !== companyId) return STALE;
    const controller = new AbortController();
    requestControllers.current.add(controller);
    try {
      const response = method === "put"
        ? await http.put(path, body, { ...config, signal: controller.signal })
        : await http.get(path, { ...config, signal: controller.signal });
      if (!alive.current || controller.signal.aborted || localStorage.getItem("vg_company") !== companyId) return STALE;
      return response?.data;
    } finally {
      requestControllers.current.delete(controller);
    }
  };

  const loadSettings = async () => {
    setSettingsLoading(true); setSettingsError("");
    try {
      const data = await request(ROUTES.settings);
      if (data === STALE) return;
      if (!data || typeof data !== "object") throw new Error("The automation settings response was empty or invalid.");
      setSettings({ ...DEFAULTS, ...data });
    } catch (error) { if (isCurrentCompany()) setSettingsError(getError(error, "load automation settings")); }
    finally { if (alive.current && localStorage.getItem("vg_company") === companyId) setSettingsLoading(false); }
  };
  const loadContacts = async () => {
    setContactsLoading(true); setContactsError("");
    try {
      const data = await request(ROUTES.contacts);
      if (data === STALE) return;
      if (!Array.isArray(data)) throw new Error("The contacts response was invalid.");
      setContacts(data);
    } catch (error) { if (isCurrentCompany()) setContactsError(getError(error, "load contacts")); }
    finally { if (alive.current && localStorage.getItem("vg_company") === companyId) setContactsLoading(false); }
  };
  const loadCatalogues = async () => {
    setCataloguesLoading(true); setCataloguesError("");
    try {
      const data = await request(ROUTES.catalogues);
      if (data === STALE) return;
      if (!Array.isArray(data)) throw new Error("The catalogue response was invalid.");
      setCatalogues(data);
    } catch (error) { if (isCurrentCompany()) setCataloguesError(getError(error, "load catalogues")); }
    finally { if (alive.current && localStorage.getItem("vg_company") === companyId) setCataloguesLoading(false); }
  };
  const loadHistory = async () => {
    setHistoryLoading(true); setHistoryError("");
    try {
      const data = await request(ROUTES.history);
      if (data === STALE) return;
      if (!Array.isArray(data)) throw new Error("The submission history response was invalid.");
      setHistory(data);
    } catch (error) { if (isCurrentCompany()) setHistoryError(getError(error, "load submission history")); }
    finally { if (alive.current && localStorage.getItem("vg_company") === companyId) setHistoryLoading(false); }
  };
  const loadPreview = async () => {
    setPreviewLoading(true); setPreviewError("");
    try {
      const data = await request(ROUTES.preview);
      if (data === STALE) return;
      if (!data || typeof data !== "object") throw new Error("The schedule preview response was invalid.");
      setPreview(data);
    } catch (error) { if (isCurrentCompany()) setPreviewError(getError(error, "load schedule preview")); }
    finally { if (alive.current && localStorage.getItem("vg_company") === companyId) setPreviewLoading(false); }
  };

  useEffect(() => {
    requestControllers.current.forEach((controller) => controller.abort());
    requestControllers.current.clear();
    if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
    blobUrlRef.current = ""; setPreviewBlob(null); setPreviewCatalogueId(null);
    setSettings(DEFAULTS); setContacts([]); setCatalogues([]); setHistory([]); setPreview(null);
    if (!companyId || !canManage) {
      setSettingsLoading(false); setContactsLoading(false); setCataloguesLoading(false); setHistoryLoading(false); setPreviewLoading(false);
      return undefined;
    }
    loadSettings(); loadContacts(); loadCatalogues(); loadHistory(); loadPreview();
    return () => {
      requestControllers.current.forEach((controller) => controller.abort());
      requestControllers.current.clear();
    };
    // Each loader is intentionally scoped to this company identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, canManage]);

  useEffect(() => () => { if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current); }, []);

  const changeSetting = (key, value) => {
    setSettings((current) => ({ ...current, [key]: value }));
    setSavedNotice("");
  };
  const saveSettings = async (nextSettings = settings) => {
    setSavedNotice(""); setSettingsError("");
    const templateKeys = ["greeting_template", "catalogue_template", "image_caption_template", "customer_due_template", "vendor_due_template"];
    const invalidTemplate = templateKeys.find((key) => !String(nextSettings[key] || "").trim() || !/\bstop\b/i.test(nextSettings[key]));
    if (invalidTemplate) {
      setSettingsError("Each message template must have content and include a clear STOP opt-out instruction.");
      return;
    }
    setSaving(true);
    try {
      const data = await request(ROUTES.settings, {}, "put", nextSettings);
      if (data === STALE) return;
      if (!data || typeof data !== "object") throw new Error("The settings service did not return saved settings.");
      setSettings({ ...DEFAULTS, ...data });
      setSavedNotice("Daily automation settings saved for this company.");
      await loadPreview();
    } catch (error) { if (isCurrentCompany()) setSettingsError(getError(error, "save automation settings")); }
    finally { if (alive.current) setSaving(false); }
  };
  const toggleEnabled = (enabled) => saveSettings({ ...settings, enabled });

  const updateContact = async (contact, patch) => {
    const key = `${contact.kind}:${contact.id}`;
    setContactBusy((current) => ({ ...current, [key]: true })); setContactNotice("");
    const payload = {
      marketing_consent: Boolean(contact.marketing_consent),
      dues_consent: Boolean(contact.dues_consent),
      opted_out: Boolean(contact.opted_out),
      phone_override: contact.phone_override || null,
      ...patch,
    };
    try {
      const data = await request(`${ROUTES.contacts}/${encodeURIComponent(contact.kind)}/${encodeURIComponent(contact.id)}`, {}, "put", payload);
      if (data === STALE) return;
      if (!data || data.id == null) throw new Error("The contact preference update was not confirmed.");
      setContacts((items) => items.map((item) => item.kind === data.kind && String(item.id) === String(data.id) ? data : item));
      setContactNotice(`Saved preferences for ${data.name || contact.name || "contact"}.`);
      await loadPreview();
    } catch (error) {
      if (isCurrentCompany()) setContactsError(getError(error, "save contact preferences"));
    } finally { if (alive.current) setContactBusy((current) => ({ ...current, [key]: false })); }
  };

  const openCataloguePreview = async (catalogue) => {
    if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
    blobUrlRef.current = ""; setPreviewBlob(null); setPreviewCatalogueId(null); setConfirmReviewed(false);
    setCatalogueBusy((current) => ({ ...current, [catalogue.id]: true }));
    let controller;
    try {
      if (!companyId || localStorage.getItem("vg_company") !== companyId) return;
      controller = new AbortController();
      requestControllers.current.add(controller);
      const response = await http.get(`${ROUTES.catalogues}/${encodeURIComponent(catalogue.id)}/preview`, {
        responseType: "blob", signal: controller.signal,
      });
      if (!alive.current || controller.signal.aborted || localStorage.getItem("vg_company") !== companyId) return;
      const url = URL.createObjectURL(response.data);
      blobUrlRef.current = url; setPreviewBlob(url); setPreviewCatalogueId(catalogue.id);
    } catch (error) {
      if (isCurrentCompany()) setCataloguesError(getError(error, "load private catalogue preview"));
    } finally {
      if (controller) requestControllers.current.delete(controller);
      if (alive.current) setCatalogueBusy((current) => ({ ...current, [catalogue.id]: false }));
    }
  };
  const approveCatalogue = async (catalogue, approved) => {
    if (approved && !window.confirm(`Approve “${catalogue.name}” for automatic daily WhatsApp rotation? Approval is tied to this file's current content fingerprint. Review the file content first.`)) return;
    setCatalogueBusy((current) => ({ ...current, [catalogue.id]: true }));
    try {
      const data = await request(`${ROUTES.catalogues}/${encodeURIComponent(catalogue.id)}`, {}, "put", { approved });
      if (data === STALE) return;
      if (!data || data.id == null) throw new Error("The catalogue approval was not confirmed.");
      setCatalogues((items) => items.map((item) => String(item.id) === String(data.id) ? data : item));
      setConfirmReviewed(false);
      await loadPreview();
    } catch (error) { if (isCurrentCompany()) setCataloguesError(getError(error, "update catalogue approval")); }
    finally { if (alive.current) setCatalogueBusy((current) => ({ ...current, [catalogue.id]: false })); }
  };

  const counts = preview || {};
  const filteredHistory = useMemo(() => historyFilter === "all"
    ? history : history.filter((row) => String(row.status || "").toLowerCase() === historyFilter), [history, historyFilter]);
  const upcoming = [
    settings.greeting_enabled && ["09:00", settings.greeting_time, "Greeting"],
    settings.catalogue_enabled && ["09:30", settings.catalogue_time, "Catalogue link"],
    settings.catalogue_image_enabled && ["09:45", settings.catalogue_image_time, "Catalogue image"],
    settings.dues_enabled && ["10:00", settings.dues_time, "Customer & vendor dues"],
  ].filter(Boolean).sort((a, b) => String(a[1]).localeCompare(String(b[1])));

  if (!canManage) return <Card className="p-5"><p className="text-sm text-muted-foreground">You need the manage settings permission to configure WhatsApp automation.</p></Card>;
  if (!companyId) return <Card className="border-amber-200 bg-amber-50 p-5 text-sm text-amber-900">Select an active company to configure daily WhatsApp messaging.</Card>;

  return <div className="space-y-4" data-testid="whatsapp-automation">
    <Card className="overflow-hidden">
      <div className="border-b bg-muted/25 p-5 sm:p-6">
        <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-start">
          <PanelHeading eyebrow="Company messaging schedule" title="Daily contact automation" icon={Clock3}
            note="Configure the schedule once. A server-side worker handles each day's eligible messages; this browser preview never sends anything." />
          <div className={`inline-flex w-fit items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold ${settings.enabled ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-slate-200 bg-background text-muted-foreground"}`}>
            <span className={`h-2 w-2 rounded-full ${settings.enabled ? "bg-emerald-500" : "bg-slate-400"}`} />{settings.enabled ? "Automation on" : "Stopped"}
          </div>
        </div>
        <LoadState loading={settingsLoading} error={settingsError} retry={loadSettings} noun="company automation settings" />
        <div className="mt-4 grid gap-3 sm:grid-cols-[1fr_auto] sm:items-center">
          <div className="rounded-md border bg-background px-3 py-2.5 text-sm">
            <div className="font-medium">Upcoming in {settings.timezone || "Asia/Kolkata"}</div>
            <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
              {upcoming.length ? upcoming.map(([, time, label]) => <span key={label} className="font-mono">{time || "—"} <span className="font-sans">{label}</span></span>) : <span>No stages enabled.</span>}
              {settings.dues_enabled && <span>Every {settings.overdue_every_days || 7} overdue days</span>}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" onClick={() => saveSettings()} disabled={saving || settingsLoading} data-testid="whatsapp-automation-save">
              {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />}Save schedule
            </Button>
            <Button type="button" onClick={() => toggleEnabled(!settings.enabled)} disabled={saving || settingsLoading} data-testid="whatsapp-automation-toggle">
              {saving ? "Saving…" : settings.enabled ? "Stop schedule" : "Start schedule"}
            </Button>
          </div>
        </div>
        {savedNotice && <p role="status" className="mt-3 flex items-center gap-1.5 text-xs text-emerald-800"><CheckCircle2 className="h-4 w-4" />{savedNotice}</p>}
        <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs leading-relaxed text-amber-950">
          <div className="flex items-start gap-2"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /><p><strong>Publishing matters.</strong> Scheduled work runs only while the production worker is available. Autoscale may sleep; a Reserved VM needs to be published and is not enabled by this screen. After changing deployment settings, republish the VM. Schedules skip missed windows rather than send stale catch-up blasts. Provider submission is not proof of delivery.</p></div>
        </div>
        <div className="mt-3 rounded-md border bg-background px-3 py-2 text-xs text-muted-foreground">
          <strong className="text-foreground">Preview only.</strong> No send or campaign action is available here. Messages outside their 15-minute start window are skipped. Unavailable/disconnected runtime is skipped before submission.
        </div>
      </div>

      <div className="space-y-5 p-5 sm:p-6">
        <div className="grid gap-3 sm:grid-cols-3">
          <div><Label htmlFor="wa-timezone">Schedule timezone</Label><Input id="wa-timezone" value={settings.timezone || ""} onChange={(e) => changeSetting("timezone", e.target.value)} placeholder="Asia/Kolkata" className="mt-1.5 font-mono" /></div>
          <div><Label htmlFor="wa-default-country">Default country code</Label><Input id="wa-default-country" inputMode="numeric" value={settings.default_country_code || ""} onChange={(e) => changeSetting("default_country_code", e.target.value.replace(/\D/g, ""))} className="mt-1.5" /></div>
          <div><Label htmlFor="wa-overdue-days">Repeat overdue every (days)</Label><Input id="wa-overdue-days" type="number" min="1" max="365" value={settings.overdue_every_days || ""} onChange={(e) => changeSetting("overdue_every_days", Number(e.target.value))} className="mt-1.5" /></div>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <ScheduleField label="Greeting" name="greeting_time" value={settings.greeting_time} onChange={changeSetting} disabled={!settings.greeting_enabled} />
          <ScheduleField label="Catalogue link" name="catalogue_time" value={settings.catalogue_time} onChange={changeSetting} disabled={!settings.catalogue_enabled} />
          <ScheduleField label="Catalogue image" name="catalogue_image_time" value={settings.catalogue_image_time} onChange={changeSetting} disabled={!settings.catalogue_image_enabled} />
          <ScheduleField label="Customer & vendor dues" name="dues_time" value={settings.dues_time} onChange={changeSetting} disabled={!settings.dues_enabled} />
        </div>
        <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
          <Toggle checked={settings.greeting_enabled} onChange={(value) => changeSetting("greeting_enabled", value)} label="Greeting" detail="Marketing consent only" />
          <Toggle checked={settings.catalogue_enabled} onChange={(value) => changeSetting("catalogue_enabled", value)} label="Catalogue link" detail="Approved catalogue and marketing consent" />
          <Toggle checked={settings.catalogue_image_enabled} onChange={(value) => changeSetting("catalogue_image_enabled", value)} label="Catalogue image" detail="Same daily selection as link, 15 minutes later" />
          <Toggle checked={settings.dues_enabled} onChange={(value) => changeSetting("dues_enabled", value)} label="Due reminders" detail="Separate dues consent; vendors are payables we owe" />
        </div>
        <div className="grid gap-4 border-t pt-5 lg:grid-cols-2">
          {[
            ["greeting_template", "Greeting template"],
            ["catalogue_template", "Catalogue link template"],
            ["image_caption_template", "Catalogue image caption"],
            ["customer_due_template", "Customer due reminder"],
            ["vendor_due_template", "Vendor due reminder"],
          ].map(([key, label]) => <div key={key} className={key === "vendor_due_template" ? "lg:col-span-2" : ""}>
            <Label htmlFor={`wa-template-${key}`}>{label}</Label>
            <Textarea id={`wa-template-${key}`} value={settings[key] || ""} maxLength={4096} rows={key.includes("due") ? 3 : 2}
              onChange={(e) => changeSetting(key, e.target.value)} className="mt-1.5" data-testid={`whatsapp-auto-template-${key}`} />
            <p className="mt-1 text-xs text-muted-foreground">{VARIABLE_HINT}</p>
            {key === "vendor_due_template" && <p className="mt-1 text-xs text-amber-800">Vendor wording describes an amount your business owes them, not customer payables. Vendors without credit days need updated vendor financial terms before dues timing can be calculated.</p>}
          </div>)}
        </div>
      </div>
    </Card>

    <Card className="overflow-hidden">
      <div className="border-b p-5 sm:p-6">
        <PanelHeading eyebrow="Per-contact controls" title="Consent and WhatsApp numbers" icon={Users}
          note="Preferences are saved per named customer or vendor. No consent is inferred or enabled for a contact by default." />
        <div className="mt-3 flex flex-wrap gap-2 text-xs">
          <span className="rounded-full bg-muted px-2.5 py-1">{contacts.length} contacts</span>
          <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-emerald-800">{contacts.filter((item) => item.marketing_consent && !item.opted_out).length} marketing consent</span>
          <span className="rounded-full bg-muted px-2.5 py-1">{contacts.filter((item) => item.dues_consent && !item.opted_out).length} dues consent</span>
        </div>
      </div>
      <LoadState loading={contactsLoading} error={contactsError} retry={loadContacts} noun="named contacts" />
      {contactNotice && <div role="status" className="border-b bg-emerald-50 px-4 py-2 text-xs text-emerald-800">{contactNotice}</div>}
      {!contactsLoading && !contactsError && !contacts.length && <div className="p-8 text-center text-sm text-muted-foreground">No customers or vendors are available for this company.</div>}
      {contacts.length > 0 && <div className="divide-y">
        {contacts.map((contact) => <ContactConsent key={`${contact.kind}:${contact.id}`}
          contact={contact} busy={Boolean(contactBusy[`${contact.kind}:${contact.id}`])} updateContact={updateContact} />)}
      </div>}
    </Card>

    <Card className="overflow-hidden">
      <div className="border-b p-5 sm:p-6">
        <PanelHeading eyebrow="Daily rotation" title="Catalogue files" icon={FileImage}
          note="Review the private file preview before a one-time approval. The selected approved file is rotated automatically; catalogue link and image share the same daily selection." />
      </div>
      <LoadState loading={cataloguesLoading} error={cataloguesError} retry={loadCatalogues} noun="uploaded catalogues" />
      {!cataloguesLoading && !cataloguesError && !catalogues.length && <div className="p-7 text-sm text-muted-foreground">No uploaded catalogues found. Upload a catalogue in the existing catalogue workflow before enabling these stages.</div>}
      {catalogues.map((catalogue) => <div key={catalogue.id} className="border-b p-4 last:border-b-0 sm:p-5">
        <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
          <div className="min-w-0">
            <div className="truncate font-medium">{catalogue.name}</div>
            <div className="mt-1 text-xs text-muted-foreground">{catalogue.mime_type || "File"}{catalogue.updated_at ? ` · Updated ${new Date(catalogue.updated_at).toLocaleString()}` : ""}</div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${catalogue.approved ? "bg-emerald-50 text-emerald-800" : "bg-muted text-muted-foreground"}`}>
              {catalogue.approved ? "Approved for current fingerprint" : "Not approved"}
            </span>
            <Button type="button" size="sm" variant="outline" onClick={() => openCataloguePreview(catalogue)} disabled={Boolean(catalogueBusy[catalogue.id])}>
              {catalogueBusy[catalogue.id] ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Eye className="mr-1.5 h-3.5 w-3.5" />}Private preview
            </Button>
            {catalogue.approved
              ? <Button type="button" size="sm" variant="outline" onClick={() => approveCatalogue(catalogue, false)} disabled={Boolean(catalogueBusy[catalogue.id])}>Revoke approval</Button>
              : <Button type="button" size="sm" onClick={() => openCataloguePreview(catalogue)} disabled={Boolean(catalogueBusy[catalogue.id])}>Review to approve</Button>}
          </div>
        </div>
        {String(previewCatalogueId) === String(catalogue.id) && previewBlob && <div className="mt-4 rounded-md border bg-muted/20 p-3">
          <img src={previewBlob} alt={`Private preview of ${catalogue.name}`} className="mx-auto max-h-[420px] max-w-full rounded object-contain" />
          <label className="mt-3 flex items-start gap-2 border-t pt-3 text-xs">
            <input type="checkbox" checked={confirmReviewed} onChange={(e) => setConfirmReviewed(e.target.checked)} className="mt-0.5 h-4 w-4 accent-brand-600" />
            <span>I reviewed this uploaded catalogue content and authorize this exact file fingerprint for automatic daily messages.</span>
          </label>
          {!catalogue.approved && <Button type="button" size="sm" className="mt-3" disabled={!confirmReviewed || Boolean(catalogueBusy[catalogue.id])}
            onClick={() => approveCatalogue(catalogue, true)}><ShieldCheck className="mr-1.5 h-3.5 w-3.5" />Confirm approval</Button>}
          {catalogue.mime_type?.includes("pdf") && <p className="mt-2 text-xs text-muted-foreground">PDF preview shows the first page only.</p>}
        </div>}
      </div>)}
      <div className="border-t bg-muted/20 px-4 py-3 text-xs text-muted-foreground">Only uploaded catalogue images or the first page of an approved PDF are used. There is no product-photo catalogue feature. Previews are authorized, private image responses and are not public links.</div>
    </Card>

    <Card className="overflow-hidden">
      <div className="border-b p-5 sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <PanelHeading eyebrow="Live rules, no send" title="Eligibility preview" icon={ShieldCheck}
            note="Current company schedule and eligibility counts only. Refreshing this view cannot trigger a message." />
          <Button type="button" size="sm" variant="outline" onClick={loadPreview} disabled={previewLoading}><RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${previewLoading ? "animate-spin" : ""}`} />Refresh preview</Button>
        </div>
      </div>
      <div className="p-5">
        <LoadState loading={previewLoading} error={previewError} retry={loadPreview} noun="eligibility preview" />
        {preview && !previewLoading && <div>
          <div className="grid gap-3 sm:grid-cols-3">
            {[["Eligible for marketing", counts.eligible_marketing_count], ["Eligible for dues", counts.eligible_dues_count], ["Approved catalogues", counts.approved_catalogue_count]].map(([label, count]) =>
              <div key={label} className="rounded-md border bg-background px-4 py-3"><div className="text-xs text-muted-foreground">{label}</div><div className="mt-1 font-mono text-2xl font-semibold">{Number.isFinite(Number(count)) ? count : "—"}</div></div>)}
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
            <span>Timezone: <strong className="font-mono text-foreground">{preview.timezone || settings.timezone}</strong></span>
            <span>Connection: <strong className="text-foreground">{preview.connection_status || "Unknown"}</strong></span>
          </div>
          {Array.isArray(preview.notes) && preview.notes.length > 0 && <ul className="mt-3 space-y-1 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-950">{preview.notes.map((note, index) => <li key={`${index}-${note}`} className="flex gap-2"><AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />{note}</li>)}</ul>}
          <p className="mt-3 text-xs text-muted-foreground">A due reminder to a vendor describes money your business owes. Vendors without credit days need their financial terms updated separately by an authorized vendor editor.</p>
        </div>}
      </div>
    </Card>

    <Card className="overflow-hidden">
      <div className="border-b p-5 sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <PanelHeading eyebrow="Actual provider submissions" title="Submission history" icon={ArrowDownUp}
            note="Records show attempted submissions, not delivery receipts. Uncertain or reserved attempts are never presented as safe to retry." />
          <div className="flex gap-2">
            <select aria-label="Filter submission history" value={historyFilter} onChange={(e) => setHistoryFilter(e.target.value)}
              className="h-9 rounded-md border bg-background px-2 text-xs">
              <option value="all">All outcomes</option><option value="submitted">Submitted</option><option value="uncertain">Uncertain</option><option value="skipped">Skipped</option><option value="failed">Failed</option>
            </select>
            <Button type="button" size="sm" variant="outline" onClick={loadHistory} disabled={historyLoading}><RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${historyLoading ? "animate-spin" : ""}`} />Refresh</Button>
          </div>
        </div>
      </div>
      <LoadState loading={historyLoading} error={historyError} retry={loadHistory} noun="submission history" />
      {historyLoading ? null : historyError ? null : filteredHistory.length === 0
        ? <div className="p-8 text-center"><div className="mx-auto mb-2 grid h-9 w-9 place-items-center rounded-full bg-muted text-muted-foreground"><Check className="h-4 w-4" /></div><p className="text-sm font-medium">No recorded submissions</p><p className="mt-1 text-xs text-muted-foreground">{history.length ? "No records match this outcome filter." : "Submission records will appear here after the production worker records an attempt."}</p></div>
        : <div className="divide-y">
          {filteredHistory.map((row) => {
            const outcome = String(row.status || "unknown").toLowerCase();
            const pending = outcome === "uncertain" || outcome.includes("reserved");
            const submitted = outcome === "submitted";
            const label = submitted ? "Submitted · NOT delivered" : pending ? "Uncertain · NOT delivered" : outcome === "skipped" ? "Skipped · NOT delivered" : `${String(row.status || "Unknown")} · NOT delivered`;
            return <div key={row.id} className="grid gap-2 px-4 py-3 text-sm sm:grid-cols-[minmax(110px,.7fr)_minmax(180px,1fr)_minmax(110px,.7fr)_minmax(190px,1fr)] sm:items-center">
              <div><div className="font-medium">{row.day || "—"}</div><div className="text-xs text-muted-foreground">{row.stage || "Stage unknown"}</div></div>
              <div className="min-w-0"><div className="truncate font-medium">{row.contact_name || "Unknown contact"}</div><div className="text-xs text-muted-foreground">{row.party_kind === "vendor" ? "Vendor" : row.party_kind === "customer" ? "Customer" : "Contact"} · #{row.party_id ?? "—"}</div></div>
              <div><span className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-[11px] font-medium ${submitted ? "bg-emerald-50 text-emerald-800" : pending ? "bg-amber-100 text-amber-950" : "bg-muted text-muted-foreground"}`}>
                {pending ? <ShieldAlert className="h-3 w-3" /> : submitted ? <CheckCircle2 className="h-3 w-3" /> : <AlertCircle className="h-3 w-3" />}{label}</span></div>
              <div className="min-w-0 text-xs text-muted-foreground">
                {row.error && <div className="break-words text-rose-800">{row.error}</div>}
                {row.message_id && <div className="truncate font-mono">Provider ID: {row.message_id}</div>}
                <div>{row.updated_at ? new Date(row.updated_at).toLocaleString() : row.created_at ? new Date(row.created_at).toLocaleString() : ""}</div>
                {pending && <div className="mt-0.5 font-medium text-amber-900">Reserved/uncertain outcome — do not retry; this may already have been submitted.</div>}
              </div>
            </div>;
          })}
        </div>}
      <div className="border-t bg-muted/20 px-4 py-3 text-xs text-muted-foreground">At-most-once submissions are keyed per company, date, stage, and normalized recipient phone; customer and vendor dues directions stay distinct. There is intentionally no retry button because repeating an uncertain submission could duplicate a message.</div>
    </Card>
  </div>;
}

export default WhatsAppAutomation;